import {
  DataTableDataSearchDto,
  SortColumnDto,
  TableCacheKeyDto,
  TableDataDtoResponse,
} from '@asset-metrix/portal-gateway-client-js';
import {
  keepPreviousData,
  QueryFunction,
  useQuery,
} from '@tanstack/react-query';

import { extractErrorMessage } from '../../utils/errorMessage';
import {
  prependRoleId,
  QueryKeyWithRole,
  unwrapQueryKeyWithRole,
} from '../../utils/prependRoleId';
import { HttpError } from '../errors';
import { portalGatewayClient } from '../portalGatewayClient';

import { convertSortItemsToSortString } from './datatableData';

export type FetchGroupedTableDataQueryKeyParams = {
  page: number;
  pageSize: number;
  pageFilter: DataTableDataSearchDto['selection'];
  tableFilter: DataTableDataSearchDto['selection'];
  workflowStateSelection?: DataTableDataSearchDto['workflowStateSelection'];
  sortItems: SortColumnDto[];
  groupByColumnId: string;
  stakeAdjusted?: boolean;
};

export type FetchGroupedTableDataQueryKey = readonly [
  TableCacheKeyDto,
  { pageId: string },
  { widgetId: string },
  FetchGroupedTableDataQueryKeyParams,
  ...string[],
];

export const fetchGroupedTableData: QueryFunction<
  TableDataDtoResponse,
  QueryKeyWithRole<FetchGroupedTableDataQueryKey>
> = async ({ queryKey }) => {
  const unwrappedQueryKey = unwrapQueryKeyWithRole(queryKey).params;
  const { widgetId } = unwrappedQueryKey[2];
  const {
    page,
    pageSize,
    pageFilter,
    tableFilter,
    workflowStateSelection,
    sortItems,
    groupByColumnId,
    stakeAdjusted,
  } = unwrappedQueryKey[3];

  const filterSelections = {
    ...pageFilter,
    ...tableFilter,
  };

  const sort = convertSortItemsToSortString(sortItems);
  const response = await portalGatewayClient.table.searchGroupedTableData(
    widgetId,
    {
      selection: filterSelections,
      workflowStateSelection,
      groupByColumnId,
      stakeAdjusted,
    },
    {
      offset: page * pageSize,
      limit: pageSize,
      ...(sort != null && { sort }),
    }
  );

  if (!response.ok) {
    throw new HttpError(extractErrorMessage(response.error), response);
  }

  return response.data;
};

export const useGroupedTableDataQuery = (
  pageId: string,
  widgetId: string,
  cacheKey: TableCacheKeyDto,
  queryParams: FetchGroupedTableDataQueryKeyParams,
  enabled: boolean
) =>
  useQuery({
    queryKey: prependRoleId<FetchGroupedTableDataQueryKey>([
      'groupedDatatables',
      cacheKey,
      { pageId },
      { widgetId },
      queryParams,
    ]),
    queryFn: fetchGroupedTableData,
    placeholderData: keepPreviousData,
    enabled,
  });
